import { useState } from 'react';
import { UserMinus } from 'lucide-react';
import { Card } from '@/components/Card';
import { StatusBadge } from '@/components/StatusBadge';
import { useApi } from '@/hooks/useApi';
import { RevocationStatusPanel } from '@/components/RevocationStatusPanel';

interface Offboarding {
  id: string;
  employee_name: string;
  status: string;
  start_date: string;
  target_completion_date: string;
  progress: number;
  tasks: Array<{ id: string; title: string; completed: boolean; category: string }>;
}

export function OffboardingPage() {
  const { data, loading } = useApi<{ offboarding: Offboarding[] }>('offboarding');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  if (loading) return <div className="text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
        <UserMinus className="w-5 h-5 text-amber-500" /> Active Offboarding
      </h2>
      {(data?.offboarding ?? []).length === 0 ? (
        <Card><p className="text-sm text-muted-foreground">No active offboarding processes</p></Card>
      ) : (
        <div className="space-y-4">
          {data?.offboarding.map(o => (
            <Card key={o.id}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{o.employee_name}</p>
                  <p className="text-xs text-muted-foreground">Last day: {o.target_completion_date}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>
              <div className="w-full bg-surface-3 rounded-full h-2 mb-3">
                <div className="bg-amber-500 h-2 rounded-full transition-all" style={{ width: `${o.progress}%` }} />
              </div>
              <div className="space-y-1">
                {o.tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 text-xs">
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${t.completed ? 'bg-emerald-500/20 border-emerald-500 text-emerald-400' : 'border-border'}`}>
                      {t.completed && '✓'}
                    </span>
                    <span className={t.completed ? 'text-muted-foreground line-through' : 'text-foreground'}>{t.title}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}
                className="mt-3 text-xs text-red-400 hover:text-red-300"
              >
                {expandedId === o.id ? 'Hide' : 'Manage'} access revocation
              </button>
              {expandedId === o.id && (
                <div className="mt-3">
                  <RevocationStatusPanel offboardingId={o.id} />
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
